'use client';

import { CloseIcon } from '@repo/icons';
import { useBreakpoint } from '@repo/react-hooks';
import { Dialog, DialogClose, DialogContent, DialogTitle } from '@repo/ui/dialog';
import { Skeleton } from '@repo/ui/skeleton';
import { cn } from '@repo/utils/cn';
import { useDebouncedValue } from '@tanstack/react-pacer';
import { useQuery } from '@tanstack/react-query';
import React, { memo, useEffect, useState } from 'react';
import { APP_REVALIDATE_QUERY_NAMES } from '@/constants';
import { getCasinoGamesProviders, getCasinoGamesTypes } from '@/lib/fetch/casino-games/utils';
import { useAppStore } from '@/providers/AppStoreProvider';
import { useTenant } from '@/providers/TenantProvider';
import SearchGamesResults from './search-games-results';
import SearchInput from './search-input';

const SearchCasinoGamesDialog = () => {
  const tenant = useTenant();

  const { matches: isMobile } = useBreakpoint('sm', 'max');

  const { isSearchCasinoGamesDialogOpen, setIsSearchCasinoGamesDialogOpen, currentPathname } = useAppStore((state) => ({
    isSearchCasinoGamesDialogOpen: state.isSearchCasinoGamesDialogOpen,
    setIsSearchCasinoGamesDialogOpen: state.setIsSearchCasinoGamesDialogOpen,
    currentPathname: state.currentPathname,
  }));

  // Close the dialog when the user navigates to a game page
  useEffect(() => {
    if (currentPathname?.startsWith('/game')) {
      setIsSearchCasinoGamesDialogOpen(false);
    }
  }, [currentPathname]);

  // Use stale data while revalidating
  const { data: gamesTypes, isLoading: isGamesTypesLoading } = useQuery({
    queryKey: [APP_REVALIDATE_QUERY_NAMES.CASINO_GAMES_TYPES],
    queryFn: () => getCasinoGamesTypes(tenant.tenantMachineName),
    staleTime: 30 * 60 * 1000, // 30 minutes
    enabled: isSearchCasinoGamesDialogOpen,
  });

  const { data: gamesProviders, isLoading: isGamesProvidersLoading } = useQuery({
    queryKey: [APP_REVALIDATE_QUERY_NAMES.CASINO_GAMES_PROVIDERS],
    queryFn: () => getCasinoGamesProviders(tenant.tenantMachineName),
    staleTime: 30 * 60 * 1000, // 30 minutes
    enabled: isSearchCasinoGamesDialogOpen,
  });

  // State for the raw input value
  const [inputValue, setInputValue] = useState<string>('');

  // Create debounced value from input value using options object
  const [debouncedInputValue] = useDebouncedValue(inputValue, { wait: 500 });

  const finalSearchQuery = debouncedInputValue.length >= 3 ? debouncedInputValue : null;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const handleOpenChange = (state: boolean) => {
    setInputValue('');
    setIsSearchCasinoGamesDialogOpen(state);
  };

  /**
   * Prevent the dialog from closing when clicking on the content
   * This is useful when the user clicks on a game card
   * and wants to see the game details in a new tab/window
   */
  const clickDialogContentHandler = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  const contentClassNameOverride =
    'max-h-[calc(100dvh-(var(--vt-header-nav-bar-height)+var(--vt-bottom-nav-bar-height)))] sm:max-h-[90%] sm:max-w-[80%] lg:max-w-[65%] sm:top-[50%] sm:translate-y-[-50%] top-[var(--vt-header-nav-bar-height)] translate-y-0 vt-surface-darker-bg';

  return (
    <Dialog
      modal={!isMobile}
      open={isSearchCasinoGamesDialogOpen}
      onOpenChange={handleOpenChange}
    >
      <DialogContent
        className={cn(
          'z-[var(--z-search-casino-games-dialog)] flex h-full w-full flex-col gap-0',
          contentClassNameOverride
        )}
        onClick={clickDialogContentHandler}
        portalOverlayClassName={cn(isMobile ? '!bg-transparent backdrop-blur-none' : null)}
        onOpenAutoFocus={(e) => {
          // Do not allow auto focus on mobile
          if (isMobile) {
            e.stopPropagation();
            e.preventDefault();
          }
        }}
      >
        <DialogTitle className="hidden" />
        <div className="sticky top-0 z-[var(--z-search-casino-games-dialog-sticky-header)] flex flex-col p-3 vt-surface-darker-bg">
          <div className="flex space-x-2.5 rtl:space-x-reverse">
            <SearchInput handleChange={handleChange} />
            <DialogClose className="sticky w-10 flex-none rounded-full border p-[0.7rem] !vt-atoms-btn-secondary-bg vt-atoms-btn-secondary-stroke hover:opacity-100 focus:outline-none focus:ring-1 focus:ring-offset-1 disabled:pointer-events-none">
              <CloseIcon
                height="16"
                width="16"
              />
            </DialogClose>
          </div>
        </div>

        <div className="flex h-full items-start justify-center border-t vt-atoms-btn-secondary-stroke vt-tab-item-secondary-bg">
          {isGamesTypesLoading || isGamesProvidersLoading ? (
            <div className="flex h-full w-full items-center justify-center">
              <Skeleton className="h-full w-full" />
            </div>
          ) : (
            <SearchGamesResults
              searchQuery={finalSearchQuery}
              gamesTypes={gamesTypes!}
              gamesProviders={gamesProviders!}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

SearchCasinoGamesDialog.displayName = 'SearchCasinoGamesDialog';

export default memo(SearchCasinoGamesDialog);
